import React, {
  useEffect,
  useState,
  Suspense,
  useContext,
  useRef,
} from 'react';
import {
  isMainWrapperWithoutPadding,
  isNavigationPartiallyHidden,
  isNavigationPartiallyPlusFooterHidden,
} from './utils';
import { AppBar } from './AppBar';
import { Drawer } from './Drawer';
import { HistoryObserver } from 'browser-history-observer';
import { ZDS_BUILDER_HEADER } from './constants/zds';
import styled from '@emotion/styled';
import {
  NavigationContextProvider,
  NavigationContext,
  NavigationOverrides,
} from './NavigationContext';
import { Alert } from '@platform-ui/design-system';
import { userPilotReload } from './utils/userpilotFunctions';
import { useCurrentLocation } from './hooks/useCurrentLocation';
import { useAppSwitcherData } from './hooks/useAppSwitcherData';
import { useLocalStorage } from '@rehooks/local-storage';
import { AiPanelLayoutProvider } from './Concierge/AiPanelLayoutContext';
import { AiShellSection } from './layout/AiShellSection';

const LazyEnvironmentBar = React.lazy(() => import('./EnvironmentBar'));
const LazyCopilot = React.lazy(() => import('./Copilot'));

const observer = new HistoryObserver();

const DrawerPageContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  align-items: stretch;
`;

const StickyNav = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
`;

interface MainPageContentProps {
  $padding: boolean;
  $needsPaddingLeft: string | false;
  $isZASTokenEnabled: boolean;
}

const MainPageContent = styled.div<MainPageContentProps>`
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: 100%;
  overflow-x: auto;
  box-sizing: border-box;

  ${({ $needsPaddingLeft, $isZASTokenEnabled }) =>
    $needsPaddingLeft && $isZASTokenEnabled && `padding-left: 32px;`}

  ${({ $padding }) =>
    $padding &&
    `
      & ::slotted([slot='pageContent']) {
        padding: 8px 24px 16px 24px;
      }
    `}
`;

interface PageContentProps {
  isPartialOrPartialPlus: boolean;
  isZASTokenEnabled: boolean;
  isMainWrapperWithoutPadding: boolean;
}

const PageContent = ({
  isPartialOrPartialPlus,
  isZASTokenEnabled,
  isMainWrapperWithoutPadding,
}: PageContentProps) => {
  const { pageAlert } = useContext(NavigationContext);
  const isNavigationClosed = window.localStorage.getItem(
    'ZNavigationDrawerClosed',
  );

  return (
    <MainPageContent
      $isZASTokenEnabled={isZASTokenEnabled}
      $needsPaddingLeft={isNavigationClosed || false}
      $padding={!isPartialOrPartialPlus && !isMainWrapperWithoutPadding}
    >
      <slot name="breadcrumbContent" />
      {pageAlert && <Alert {...pageAlert} />}
      <slot name="pageContent" />
    </MainPageContent>
  );
};

interface NavigationRootInnerProps {
  overrides?: NavigationOverrides;
  hideEnvironmentBar?: boolean;
  hideAiAgent?: boolean;
  suppressBillingEnvironmentBanner?: boolean;
}

export const NavigationRootInner = ({
  overrides = {},
  hideEnvironmentBar = false,
  hideAiAgent = false,
  suppressBillingEnvironmentBanner = false,
}: NavigationRootInnerProps) => {
  const { href } = useCurrentLocation();

  const { isEnabled: isZASTokenEnabled, data: appSwitcherData } =
    useAppSwitcherData();
  const navHeaderRef = useRef<HTMLDivElement>(null);

  const ZNavigationDrawerClosed = 'ZNavigationDrawerClosed';

  const defineDrawerState = () => {
    return !window.localStorage.getItem(ZNavigationDrawerClosed);
  };

  const [drawerOpen, setDrawerOpen] = useState(defineDrawerState);
  /* Partially hidden means all elements hidden except the:
   * - breadcrumbs
   * - environment banner
   * - footer banner
   */
  const [zNavPartiallyHidden, setZNavPartiallyHidden] = useState(() =>
    isNavigationPartiallyHidden(href),
  );
  const [zNavPartiallyPlusFooterHidden, setZNavPartiallyPlusFooterHidden] =
    useState(
      () =>
        isNavigationPartiallyPlusFooterHidden(href) ||
        !!document.getElementById(ZDS_BUILDER_HEADER),
    );
  const [zNavMainWrapperWithoutPadding, setZNavMainWrapperWithoutPadding] =
    useState(() => isMainWrapperWithoutPadding(href));

  const [, rerenderOnHistoryChange] = useState(0);

  useEffect(() => {
    document.body.style.overscrollBehaviorY = 'none';

    const redirectLegacyZuoraLabsAdminPath = () => {
      if (window.location.pathname !== '/one-id/admin/zuora-labs') {
        return;
      }
      window.history.replaceState(
        window.history.state,
        '',
        `/one-id/admin/zuora-ai${window.location.search}${window.location.hash}`,
      );
    };

    redirectLegacyZuoraLabsAdminPath();

    const unsubscribe = observer.onHistoryChange((payload) => {
      redirectLegacyZuoraLabsAdminPath();
      if (payload?.type === 'load') {
        return;
      }
      userPilotReload();
      rerenderOnHistoryChange((prevValue) => prevValue + 1);
      if (payload?.url) {
        setZNavPartiallyHidden(isNavigationPartiallyHidden(payload?.url));
        setZNavPartiallyPlusFooterHidden(
          isNavigationPartiallyPlusFooterHidden(payload?.url),
        );
        setZNavMainWrapperWithoutPadding(
          isMainWrapperWithoutPadding(payload?.url),
        );
      }
    });
    return unsubscribe;
  }, []);

  const isPartialOrPartialPlus =
    zNavPartiallyHidden || zNavPartiallyPlusFooterHidden || hideEnvironmentBar;

  const [, setIsDrawerClosed, deleteIsDrawerClosed] = useLocalStorage<boolean>(
    'ZNavigationDrawerClosed',
  );

  const toggleDrawer = () => {
    if (drawerOpen) {
      setIsDrawerClosed(true);
    } else {
      deleteIsDrawerClosed();
    }
    setDrawerOpen((prev) => !prev);
  };

  const pageContentNode = (
    <PageContent
      isPartialOrPartialPlus={isPartialOrPartialPlus}
      isZASTokenEnabled={isZASTokenEnabled}
      isMainWrapperWithoutPadding={zNavMainWrapperWithoutPadding}
    />
  );

  return (
    <>
      <NavigationContextProvider
        overrides={overrides}
        suppressBillingEnvironmentBanner={suppressBillingEnvironmentBanner}
      >
        <AiPanelLayoutProvider>
          <StickyNav ref={navHeaderRef} className="nav-height">
            {!hideEnvironmentBar && (
              <Suspense fallback={<React.Fragment />}>
                <LazyEnvironmentBar />
              </Suspense>
            )}
            {isPartialOrPartialPlus ? null : (
              <AppBar
                {...{
                  drawerOpen,
                  toggleDrawer,
                  isZASTokenEnabled,
                  appSwitcherData,
                }}
              />
            )}
            <div id="belowMainPageContent" />
          </StickyNav>
          <DrawerPageContainer>
            {isPartialOrPartialPlus ? null : (
              <Drawer
                {...{
                  drawerOpen,
                  navHeaderRef,
                }}
              />
            )}
            <AiShellSection
              mainContent={pageContentNode}
              hideAiAgent={hideAiAgent}
            />
            <Suspense fallback={<React.Fragment />}>
              <LazyCopilot />
            </Suspense>
          </DrawerPageContainer>
        </AiPanelLayoutProvider>
      </NavigationContextProvider>
    </>
  );
};
